import React from 'react';
import { Sparkles, Quote } from 'lucide-react';

// This component is rendered off-screen (or in a hidden container) 
// and then captured by html2canvas.
export const SparkShareCard = React.forwardRef(({ spark, author, theme = 'dark', getEmoteUrl }, ref) => {

    if (!spark) return null;

    // Helper to bust cache for CORS
    const bustCache = (url) => {
        if (!url) return null;
        if (url.startsWith('data:')) return url; // Don't bust data URIs
        return url + (url.includes('?') ? '&' : '?') + 't=' + new Date().getTime();
    };

    // Theme Logic
    const currentTheme = localStorage.getItem('theme') || 'dark';
    const isDark = currentTheme === 'dark';

    const styles = {
        bg: isDark ? '#1E1E24' : '#ffffff',
        bgGradient: isDark ? 'linear-gradient(135deg, #1f1f23 0%, #111114 100%)' : 'linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%)',
        textPrimary: isDark ? '#e2e8f0' : '#1f2937',
        textSecondary: isDark ? '#a1a1aa' : '#6b7280',
        borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        quoteColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)', // Matches top/bottom opacity
        quoteColorFaint: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
        fadeOverlay: isDark ? 'linear-gradient(to bottom, transparent 0%, #111114 90%)' : 'linear-gradient(to bottom, transparent 0%, #f3f4f6 90%)',
        logoOpacity: isDark ? 0.9 : 0.8
    };

    // --- Content Parser (Simplified for Card) ---
    const renderContent = () => {
        if (!spark.content) return null;

        const parts = spark.content.split(/(\(media:\d+\)|\(image:\d+\)|:[a-zA-Z0-9_]+:)/g);

        return parts.map((part, index) => {
            // 1. Handle Media Tokens: (media:1) or (image:1)
            const mediaMatch = part.match(/\((?:media|image):(\d+)\)/);
            if (mediaMatch) {
                const mediaIndex = parseInt(mediaMatch[1]) - 1;
                const imageUrl = spark.images?.[mediaIndex] || spark.imageUrl; // Fallback

                if (imageUrl) {
                    // YOUTUBE / VIDEO CHECK
                    const isVideo = imageUrl.includes('youtube.com') || imageUrl.includes('youtu.be') || imageUrl.includes('ytimg.com');

                    if (isVideo) {
                        return React.createElement('div', {
                            key: index,
                            style: {
                                margin: '15px 0',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                background: isDark ? '#333' : '#e5e7eb',
                                height: '200px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                border: isDark ? '1px solid #444' : '1px solid #d1d5db'
                            }
                        },
                            React.createElement(Sparkles, { size: 48, color: '#ef4444' }),
                            React.createElement('span', { style: { marginTop: '10px', color: isDark ? '#ccc' : '#4b5563', fontSize: '14px' } }, "Video Content")
                        );
                    }

                    // REGULAR IMAGE
                    return React.createElement('div', {
                        key: index,
                        style: {
                            margin: '15px 0',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                            display: 'flex',
                            justifyContent: 'center'
                        }
                    },
                        React.createElement('img', {
                            src: bustCache(imageUrl),
                            style: {
                                maxWidth: '100%',
                                maxHeight: '250px', // Limit height on card
                                objectFit: 'cover',
                                display: 'block'
                            },
                            crossOrigin: "anonymous" // CRITICAL
                        })
                    );
                }
                return null;
            }

            // 2. Handle Emotes: :SparkLove:
            const emoteMatch = part.match(/^:([a-zA-Z0-9_]+):$/);
            if (emoteMatch) {
                const emoteCode = emoteMatch[1];
                const url = getEmoteUrl(part) || getEmoteUrl(emoteCode); // Try both :Code: and Code

                if (url) {
                    return React.createElement('img', {
                        key: index,
                        src: bustCache(url),
                        alt: part,
                        style: {
                            width: '32px', // Card Emote Size
                            height: '32px',
                            verticalAlign: 'middle',
                            margin: '0 4px',
                            display: 'inline-block'
                        },
                        crossOrigin: "anonymous"
                    });
                }
            }

            // 3. Regular Text
            if (part) {
                return React.createElement('span', { key: index }, part);
            }
            return null;
        });
    };

    return React.createElement('div', {
        ref: ref,
        style: {
            width: '600px', // Fixed width for consistent generation
            minHeight: '350px',
            maxHeight: '520px', // CAP HEIGHT as requested
            backgroundColor: styles.bg,
            backgroundImage: styles.bgGradient,
            color: styles.textPrimary,
            padding: '40px',
            borderRadius: '0',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            fontFamily: 'Inter, sans-serif',
            overflow: 'hidden' // Cut off extra content
        }
    },
        // Decorative Background Elements
        React.createElement('div', {
            style: {
                position: 'absolute', top: '-50px', right: '-50px',
                width: '200px', height: '200px',
                background: 'radial-gradient(circle, rgba(236,72,153,0.15) 0%, rgba(0,0,0,0) 70%)',
                borderRadius: '50%'
            }
        }),
        React.createElement('div', {
            style: {
                position: 'absolute', bottom: '-20px', left: '-20px',
                width: '150px', height: '150px',
                background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, rgba(0,0,0,0) 70%)',
                borderRadius: '50%'
            }
        }),

        // FADE OUT OVERLAY (To show there is "more")
        React.createElement('div', {
            style: {
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '120px',
                background: styles.fadeOverlay,
                zIndex: 3,
                pointerEvents: 'none'
            }
        }),

        // Header: Author Info
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '15px', position: 'relative', zIndex: 4 } }, // zIndex high for clarity
            React.createElement('img', {
                src: bustCache(spark.authorAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${spark.author}`),
                style: { width: '64px', height: '64px', borderRadius: '50%', border: '2px solid #ec4899' },
                crossOrigin: "anonymous"
            }),
            React.createElement('div', {},
                React.createElement('h3', { style: { margin: 0, fontSize: '20px', fontWeight: 'bold' } }, spark.author),
                React.createElement('span', { style: { fontSize: '14px', color: styles.textSecondary } }, "@SparkInspo")
            )
        ),

        // Main Content
        React.createElement('div', {
            style: {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                padding: '30px 0',
                position: 'relative',
                zIndex: 2,
                overflow: 'hidden', // CRITICAL: Clip the content here
                minHeight: 0 // CRITICAL: Allow flex child to shrink below content size
            }
        },
            // Top Quote (Text - consistent with Bottom)
            React.createElement('span', {
                style: {
                    fontSize: '80px',
                    lineHeight: '1',
                    fontFamily: 'sans-serif',
                    fontWeight: 'bold',
                    color: styles.quoteColor,
                    marginBottom: '-20px', // Pull it closer since line-height can be tall
                    display: 'block',
                    flexShrink: 0,
                    userSelect: 'none'
                }
            }, '“'),
            React.createElement('div', {
                style: {
                    fontSize: spark.content.length > 100 ? '24px' : '32px',
                    fontWeight: '500',
                    lineHeight: '1.4',
                    color: styles.textPrimary,
                }
            }, renderContent()),
        ),

        // Closing Quote (Text Backup - Styled to match Icon)
        React.createElement('span', {
            style: {
                position: 'absolute',
                bottom: '45px',
                right: '30px',
                fontSize: '80px',
                lineHeight: '1',
                fontFamily: 'sans-serif', // Matches the geometric look better than serif
                fontWeight: 'bold',
                color: styles.quoteColor,
                zIndex: 20,
                pointerEvents: 'none',
                userSelect: 'none'
            }
        }, '”'),

        // PINNED: Inspired by Muse (Absolute Bottom Left)
        spark.inspiration && React.createElement('div', {
            style: {
                position: 'absolute',
                bottom: '25px',
                left: '30px',
                maxWidth: '380px', // Avoid hitting the logo area
                zIndex: 15,
                background: styles.bg, // Solid background for legibility
                padding: '12px 16px',
                borderRadius: '12px',
                border: '1px solid ' + styles.borderColor,
                borderLeft: '4px solid #8b5cf6', // Primary Accent
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)', // Lift it up
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
            }
        },
            React.createElement('div', {
                style: {
                    fontSize: '11px',
                    color: '#8b5cf6',
                    textTransform: 'uppercase',
                    fontWeight: '800',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }
            },
                React.createElement(Sparkles, { size: 12 }),
                "Inspired by Muse"
            ),
            React.createElement('div', {
                style: {
                    fontSize: '14px',
                    fontStyle: 'italic',
                    color: styles.textSecondary,
                    lineHeight: '1.4',
                    display: '-webkit-box',
                    WebkitLineClamp: 2, // Limit to 2 lines so it doesn't grow huge
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                }
            }, `"${spark.inspiration}"`)
        ),

        // Logo (Absolute - Bottom Right Corner)
        React.createElement('img', {
            src: '/images/logo.png',
            style: {
                position: 'absolute',
                bottom: '15px', // Hugs the bottom
                right: '30px',  // Aligned with content padding approx
                height: '35px',
                width: 'auto',
                opacity: styles.logoOpacity,
                zIndex: 20
            },
            crossOrigin: "anonymous"
        })
    );
});
